import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Calendar, 
  Trophy, 
  Lightbulb, 
  Target,
  Loader2,
  Plus,
  ChevronLeft,
  ChevronRight,
  Smile,
  Frown,
  Meh,
  Zap
} from 'lucide-react';
import VoiceInput from '../components/VoiceInput';

const MOODS = [
  { value: 'motivated', label: 'Motivated', icon: Zap, color: 'text-green-400' },
  { value: 'inspired', label: 'Inspired', icon: Smile, color: 'text-[#D4AF37]' },
  { value: 'challenged', label: 'Challenged', icon: Meh, color: 'text-yellow-400' },
  { value: 'overwhelmed', label: 'Overwhelmed', icon: Frown, color: 'text-red-400' },
];

const MonthlyJournal = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [currentEntry, setCurrentEntry] = useState({
    month: getCurrentMonth(),
    accomplishments: [''],
    content_published: [''],
    lessons_learned: [''],
    challenges: [''],
    results: '',
    goals_for_next_month: [''],
    mood: ''
  });

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  function formatMonth(monthStr) {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    fetchEntry(selectedMonth);
  }, [selectedMonth]);

  const fetchEntries = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/journal/entries`, { headers });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  const fetchEntry = async (month) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/journal/entry/${month}`, { headers });
      if (response.data.entry) {
        setCurrentEntry({
          ...response.data.entry,
          accomplishments: response.data.entry.accomplishments?.length ? response.data.entry.accomplishments : [''],
          content_published: response.data.entry.content_published?.length ? response.data.entry.content_published : [''],
          lessons_learned: response.data.entry.lessons_learned?.length ? response.data.entry.lessons_learned : [''],
          challenges: response.data.entry.challenges?.length ? response.data.entry.challenges : [''],
          goals_for_next_month: response.data.entry.goals_for_next_month?.length ? response.data.entry.goals_for_next_month : [''],
        });
      } else {
        setCurrentEntry({
          month,
          accomplishments: [''],
          content_published: [''],
          lessons_learned: [''],
          challenges: [''],
          results: '',
          goals_for_next_month: [''],
          mood: ''
        });
      }
    } catch (error) {
      console.error('Error fetching entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const entryData = {
        ...currentEntry,
        accomplishments: currentEntry.accomplishments.filter(a => a.trim()),
        content_published: currentEntry.content_published.filter(c => c.trim()),
        lessons_learned: currentEntry.lessons_learned.filter(l => l.trim()),
        challenges: currentEntry.challenges.filter(c => c.trim()),
        goals_for_next_month: currentEntry.goals_for_next_month.filter(g => g.trim()),
      };
      
      await axios.post(`${API}/journal/entry`, entryData, { headers });
      toast.success('Journal entry saved!');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const updateListItem = (field, index, value) => {
    const updated = [...currentEntry[field]];
    updated[index] = value;
    setCurrentEntry({ ...currentEntry, [field]: updated });
  };

  const addListItem = (field) => {
    setCurrentEntry({ ...currentEntry, [field]: [...currentEntry[field], ''] });
  };

  const removeListItem = (field, index) => {
    if (currentEntry[field].length > 1) {
      const updated = currentEntry[field].filter((_, i) => i !== index);
      setCurrentEntry({ ...currentEntry, [field]: updated });
    }
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const ListEditor = ({ field, label, icon: Icon, placeholder }) => (
    <div className="card-premium p-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon className="text-[#D4AF37]" size={20} />
        <h3 className="font-serif text-lg text-white">{label}</h3>
      </div>
      <div className="space-y-3">
        {currentEntry[field].map((item, i) => (
          <div key={i} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => updateListItem(field, i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] rounded-none px-0 py-2 text-white placeholder:text-[#52525B]"
            />
            {currentEntry[field].length > 1 && (
              <button
                onClick={() => removeListItem(field, i)}
                className="text-[#52525B] hover:text-red-400 transition-colors"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          onClick={() => addListItem(field)}
          className="text-[#D4AF37] text-sm flex items-center gap-1 hover:text-[#E5C585] transition-colors"
        >
          <Plus size={14} /> Add another
        </button>
      </div>
    </div>
  );

  return (
    <DashboardLayout title="My Monthly Progress">
      <div className="max-w-4xl mx-auto">
        {/* Month Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 text-[#52525B] hover:text-[#D4AF37] transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          
          <div className="text-center">
            <p className="text-[#D4AF37] text-xs font-accent tracking-widest mb-1">JOURNAL FOR</p>
            <h2 className="font-serif text-3xl text-white">{formatMonth(selectedMonth)}</h2>
          </div>
          
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 text-[#52525B] hover:text-[#D4AF37] transition-colors"
            disabled={selectedMonth >= getCurrentMonth()}
          >
            <ChevronRight size={24} className={selectedMonth >= getCurrentMonth() ? 'opacity-30' : ''} />
          </button>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Mood Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-premium p-6"
            >
              <h3 className="font-serif text-lg text-white mb-4">How was this month?</h3>
              <div className="flex gap-3 flex-wrap">
                {MOODS.map((mood) => (
                  <button
                    key={mood.value}
                    onClick={() => setCurrentEntry({ ...currentEntry, mood: mood.value })}
                    className={`flex items-center gap-2 px-4 py-2 border transition-all ${
                      currentEntry.mood === mood.value
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-[#27272A] hover:border-[#D4AF37]/50'
                    }`}
                  >
                    <mood.icon size={18} className={mood.color} />
                    <span className="text-white text-sm">{mood.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Accomplishments */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <ListEditor field="accomplishments" label="Accomplishments" icon={Trophy} placeholder="What did you achieve?" />
            </motion.div>

            {/* Content Published */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <ListEditor field="content_published" label="Content Published" icon={BookOpen} placeholder="Content you created or published" />
            </motion.div>

            {/* Lessons Learned */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <ListEditor field="lessons_learned" label="Lessons Learned" icon={Lightbulb} placeholder="What did you learn?" />
            </motion.div>

            {/* Challenges */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <ListEditor field="challenges" label="Challenges Faced" icon={Target} placeholder="What was challenging?" />
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="card-premium p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="text-[#E5C585]" size={20} />
                <h3 className="font-serif text-lg text-white">Results & Outcomes</h3>
              </div>
              <div className="relative">
                <Textarea
                  value={currentEntry.results}
                  onChange={(e) => setCurrentEntry({ ...currentEntry, results: e.target.value })}
                  placeholder="Describe your overall results and any metrics..."
                  className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[100px] text-white placeholder:text-[#52525B]"
                />
                <div className="absolute right-3 bottom-3">
                  <VoiceInput 
                    onTranscription={(text) => {
                      const current = currentEntry.results || '';
                      setCurrentEntry({ ...currentEntry, results: current ? `${current} ${text}` : text });
                    }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Goals for Next Month */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <ListEditor field="goals_for_next_month" label="Goals for Next Month" icon={Target} placeholder="What do you want to achieve next?" />
            </motion.div>

            {/* Save Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="flex justify-end"
            >
              <Button
                onClick={handleSave}
                disabled={saving}
                className="btn-gold px-8 py-4 flex items-center gap-2"
                data-testid="save-journal"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <BookOpen size={18} />
                    Save Journal Entry
                  </>
                )}
              </Button>
            </motion.div>

            {/* Previous Entries */}
            {entries.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-12"
              >
                <h3 className="font-serif text-xl text-white mb-4">Previous Entries</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {entries.slice(0, 6).map((entry) => (
                    <button
                      key={entry.month}
                      onClick={() => setSelectedMonth(entry.month)}
                      className={`card-premium p-4 text-left hover:border-[#D4AF37]/50 transition-colors ${
                        selectedMonth === entry.month ? 'border-[#D4AF37]' : ''
                      }`}
                    >
                      <p className="text-[#D4AF37] text-xs font-accent">{formatMonth(entry.month)}</p>
                      <p className="text-white font-serif mt-1">{entry.accomplishments?.[0]?.slice(0, 40)}...</p>
                      {entry.mood && (
                        <span className="text-[#52525B] text-xs capitalize mt-2 block">{entry.mood}</span>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MonthlyJournal;
